/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { Typography2 } from '@coursera/cds-core';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import EnterCard from 'bundles/account-settings/components/sections/payment-method/EnterCard';
import ViewCurrentCard from 'bundles/account-settings/components/sections/payment-method/ViewCurrentCard';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import EnterCardFormStore from 'bundles/account-settings/stores/EnterCardFormStore';
import type { PaymentWallet } from 'bundles/account-settings/types/Payment';

import _t from 'i18n!nls/account-settings';

const { PaymentMethodSectionDisplayMode } = SettingsConstants;

type Props = {
  wallets?: Array<PaymentWallet> | null;
  creditCardPreferences?: Pick<
    PaymentWallet,
    'id' | 'creditCardExpiration' | 'creditCardLastFourDigits' | 'creditCardType' | 'isValid'
  > | null;
  showDeleteCreditCardModal?: boolean;
  updateStatusDelete: {
    state: string;
  };
  activeWalletId?: number | null;
  userId?: number;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-50);
  `,
};

const CreditCardSettings = ({
  wallets,
  creditCardPreferences,
  showDeleteCreditCardModal,
  updateStatusDelete,
  activeWalletId,
  userId,
}: Props) => {
  const isWalletUpdate = useFluxibleStore(EnterCardFormStore, (store) => !!store.getPaymentWalletId());
  const headingRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (userId) {
      SettingsActionCreators.fetchCreditCardPreferences(userId);
    }
  }, [userId]);

  const displayMode =
    isWalletUpdate || !creditCardPreferences
      ? PaymentMethodSectionDisplayMode.ENTER_CARD
      : PaymentMethodSectionDisplayMode.VIEW_CURRENT_CARD;

  const renderBody = () => {
    if (creditCardPreferences === null) {
      return (
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Loading...')}
        </Typography2>
      );
    }
    if (displayMode === PaymentMethodSectionDisplayMode.VIEW_CURRENT_CARD) {
      return (
        <ViewCurrentCard
          showDeleteCreditCardModal={showDeleteCreditCardModal}
          updateStatusDelete={updateStatusDelete}
          activeWalletId={activeWalletId}
          wallets={wallets}
          headingRef={headingRef}
        />
      );
    }
    if (displayMode === PaymentMethodSectionDisplayMode.ENTER_CARD) {
      return <EnterCard />;
    }
    return null;
  };

  return (
    <div css={styles.container} id="payment-method">
      <Typography2
        variant="subtitleLarge"
        component="h2"
        id="a11y-navigation-heading"
        tabIndex={-1}
        ref={headingRef}
        css={styles.heading}
      >
        {_t('Payment')}
      </Typography2>
      {renderBody()}
    </div>
  );
};

export default CreditCardSettings;
